import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { importFromProcore, listFromProcore, sendToProcore } from '../procoreActions';
import { asList, getJson, integrationError, postJson, statusClass, integrationKind, integrationLabel, isErpIntegration, isProjectToolIntegration, isSyncableIntegration } from '../helpers';

function ProcoreMark() {
  return (
    <svg className="pc-procore-mark" viewBox="0 0 16 16" aria-label="Synced with Procore">
      <title>Synced with Procore</title>
      <polygon points="8,1 14.5,4.5 14.5,11.5 8,15 1.5,11.5 1.5,4.5" fill="hsl(19, 100%, 60%)" />
    </svg>
  );
}

function mappedVuids(mappings, integrationVuid, objectType) {
  const ids = new Set();
  mappings.forEach((mapping) => {
    if (mapping.integration_vuid !== integrationVuid) return;
    if (objectType && mapping.object_type !== objectType) return;
    if (mapping.object_vuid) ids.add(String(mapping.object_vuid));
  });
  return ids;
}

export default function DirectoryPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncOpen, setSyncOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([getJson('/api/vendors'), getJson('/api/customers'), getJson('/api/employees')])
      .then(([vendors, customers, employees]) => {
        if (cancelled) return;
        const vendorRows = asList(vendors).map((row) => ({
          id: row.vuid,
          href: `/procore/directory/vendors/${row.vuid}`,
          name: row.vendor_name || row.company_name,
          number: row.vendor_number,
          type: 'Vendor',
          objectType: 'vendor',
          status: row.status || 'active',
        }));
        const customerRows = asList(customers).map((row) => ({
          id: row.vuid,
          href: `/procore/directory/customers/${row.vuid}`,
          name: row.customer_name || row.company_name,
          number: row.customer_number,
          type: 'Customer',
          objectType: 'customer',
          status: row.status || 'active',
        }));
        const employeeRows = asList(employees).map((row) => ({
          id: row.vuid,
          href: `/procore/directory/employees/${row.vuid}`,
          name: row.employee_name || `${row.first_name || ''} ${row.last_name || ''}`.trim(),
          number: row.employee_id || row.employee_number,
          type: 'Employee',
          objectType: 'employee',
          status: row.status || 'active',
        }));
        setRows(
          [...vendorRows, ...customerRows, ...employeeRows].sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
          )
        );
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Unable to load directory');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    Promise.all([
      getJson('/api/integrations'),
      getJson('/api/external-system-ids', { object_type: 'vendor' }),
      getJson('/api/external-system-ids', { object_type: 'customer' }),
      getJson('/api/external-system-ids', { object_type: 'employee' }),
    ])
      .then(([integrationRows, vendorMaps, customerMaps, employeeMaps]) => {
        if (cancelled) return;
        setIntegrations(
          asList(integrationRows).filter((row) => {
            if (String(row.status || '').toLowerCase() !== 'active') return false;
            return isSyncableIntegration(row);
          })
        );
        setMappings([...asList(vendorMaps), ...asList(customerMaps), ...asList(employeeMaps)]);
      })
      .catch(() => {
        if (!cancelled) setMappings([]);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const procoreVuids = useMemo(() => {
    const procoreIds = new Set(
      integrations.filter((row) => isProjectToolIntegration(row)).map((row) => row.vuid)
    );
    const ids = new Set();
    mappings.forEach((mapping) => {
      const isProcore = isProjectToolIntegration(mapping.integration) || procoreIds.has(mapping.integration_vuid);
      if (!isProcore || !mapping.object_vuid) return;
      ids.add(String(mapping.object_vuid));
    });
    return ids;
  }, [mappings, integrations]);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [row.name, row.number, row.type, row.status].some((value) => String(value || '').toLowerCase().includes(term))
    );
  }, [rows, query]);

  return (
    <div className="pc-page">
      <div className="pc-crumb">Company / Directory</div>
      <div className="pc-title-row">
        <h1>Directory</h1>
        <button type="button" className="pc-btn" onClick={() => setSyncOpen(true)}>
          Sync
        </button>
      </div>
      <div className="pc-toolbar">
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter directory" />
      </div>
      <div className="pc-card">
        {loading ? (
          <div className="pc-empty">Loading…</div>
        ) : error ? (
          <div className="pc-empty">{error}</div>
        ) : visible.length === 0 ? (
          <div className="pc-empty">No directory records to show.</div>
        ) : (
          <table className="pc-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Number</th>
                <th>Type</th>
                <th>Status</th>
                <th>Procore</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr
                  key={`${row.objectType}-${row.id}`}
                  className="pc-clickable"
                  onClick={() => navigate(row.href)}
                >
                  <td>
                    <Link to={row.href} onClick={(event) => event.stopPropagation()}>
                      {row.name || '—'}
                    </Link>
                  </td>
                  <td>{row.number || '—'}</td>
                  <td>{row.type}</td>
                  <td>
                    <span className={`pc-pill ${statusClass(row.status)}`}>{row.status || '—'}</span>
                  </td>
                  <td className="pc-sync-cell">
                    {procoreVuids.has(String(row.id)) ? <ProcoreMark /> : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {syncOpen ? (
        <DirectorySyncModal
          integrations={integrations}
          mappings={mappings}
          rows={rows}
          onClose={() => setSyncOpen(false)}
          onComplete={() => {
            setSyncOpen(false);
            setRefreshKey((value) => value + 1);
          }}
        />
      ) : null}
    </div>
  );
}

function DirectorySyncModal({ integrations, mappings, rows, onClose, onComplete }) {
  const [integrationVuid, setIntegrationVuid] = useState(integrations[0]?.vuid || '');
  const [direction, setDirection] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState('');
  const [flash, setFlash] = useState(null);

  const integration = integrations.find((row) => row.vuid === integrationVuid) || integrations[0];
  const sendAllowed = isProjectToolIntegration(integration);
  const receiveAllowed = isProjectToolIntegration(integration) || isErpIntegration(integration);

  const resetChoice = () => {
    setDirection('');
    setItems([]);
    setSelected([]);
    setFlash(null);
  };

  useEffect(() => {
    resetChoice();
  }, [integrationVuid]);

  const loadItems = async (nextDirection) => {
    if (!integration) return;
    setBusy('load');
    setFlash(null);
    try {
      if (isProjectToolIntegration(integration) && nextDirection === 'send') {
        const sendKinds = [
          { objectType: 'vendor', sendKind: 'vendor', payloadKey: 'vendor_vuid' },
          { objectType: 'customer', sendKind: 'customer', payloadKey: 'customer_vuid' },
          { objectType: 'employee', sendKind: 'employee', payloadKey: 'employee_vuid' },
        ];
        const next = [];
        sendKinds.forEach((itemKind) => {
          const synced = mappedVuids(mappings, integration.vuid, itemKind.objectType);
          rows
            .filter((row) => row.objectType === itemKind.objectType && !synced.has(String(row.id)))
            .forEach((row) => {
              next.push({
                id: `${itemKind.objectType}-${row.id}`,
                title: row.name,
                subtitle: [row.number, row.type].filter(Boolean).join(' · '),
                sendKind: itemKind.sendKind,
                payload: { [itemKind.payloadKey]: row.id },
              });
            });
        });
        setItems(next);
        setSelected(next.map((row) => row.id));
      } else if (isProjectToolIntegration(integration) && nextDirection === 'receive') {
        const { items: remote, warnings } = await listFromProcore('employees', integration.vuid);
        const next = remote.map((row) => ({
          id: row.id,
          title: row.title,
          subtitle: row.subtitle || 'Employee',
          action: 'receive-employee',
          raw: row,
        }));
        setItems(next);
        setSelected(next.map((row) => row.id));
        if (warnings?.length) setFlash({ type: 'err', text: warnings.slice(0, 4).join('\n') });
      } else if (isErpIntegration(integration) && integrationKind(integration) === 'qbo' && nextDirection === 'receive') {
        const [vendorData, customerData] = await Promise.all([
          getJson('/api/vendors/list-from-qbo', { integration_vuid: integration.vuid }),
          getJson('/api/customers/list-from-qbo', { integration_vuid: integration.vuid }),
        ]);
        const next = [
          ...asList(vendorData?.vendors).map((row) => ({
            id: `vendor-${row.id}`,
            title: row.name,
            subtitle: [row.number, 'Vendor'].filter(Boolean).join(' · '),
            action: 'receive-qbo-vendor',
            qboId: String(row.id),
          })),
          ...asList(customerData?.customers).map((row) => ({
            id: `customer-${row.id}`,
            title: row.name,
            subtitle: [row.number, 'Customer'].filter(Boolean).join(' · '),
            action: 'receive-qbo-customer',
            qboId: String(row.id),
          })),
        ];
        setItems(next);
        setSelected(next.map((row) => row.id));
      } else {
        setItems([]);
        setSelected([]);
      }
    } catch (err) {
      setItems([]);
      setSelected([]);
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  const pickDirection = (nextDirection) => {
    setDirection(nextDirection);
    loadItems(nextDirection);
  };

  const toggleItem = (id) => {
    setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  const runSync = async () => {
    if (!integration || !direction) return;
    const chosen = items.filter((row) => selected.includes(row.id));
    if (!chosen.length) {
      setFlash({ type: 'err', text: 'Select at least one record.' });
      return;
    }
    setBusy('sync');
    setFlash(null);
    const errors = [];
    let sent = 0;
    try {
      if (isProjectToolIntegration(integration) && direction === 'send') {
        for (const row of chosen) {
          try {
            await sendToProcore(integration.vuid, row.sendKind, row.payload);
            sent += 1;
          } catch (err) {
            errors.push(`${row.title}: ${integrationError(err)}`);
          }
        }
      } else if (isProjectToolIntegration(integration) && direction === 'receive') {
        const result = await importFromProcore(
          'employees',
          integration.vuid,
          undefined,
          chosen.map((row) => row.raw)
        );
        sent = chosen.length;
        (result.errors || []).forEach((item) => errors.push(item));
      } else if (isErpIntegration(integration) && integrationKind(integration) === 'qbo' && direction === 'receive') {
        const vendorIds = chosen.filter((row) => row.action === 'receive-qbo-vendor').map((row) => row.qboId);
        const customerIds = chosen.filter((row) => row.action === 'receive-qbo-customer').map((row) => row.qboId);
        const posts = [];
        if (vendorIds.length) {
          posts.push({ title: 'Vendors', path: '/api/vendors/retrieve-from-qbo', qbo_ids: vendorIds });
        }
        if (customerIds.length) {
          posts.push({ title: 'Customers', path: '/api/customers/retrieve-from-qbo', qbo_ids: customerIds });
        }
        for (const row of posts) {
          try {
            const data = await postJson(row.path, { integration_vuid: integration.vuid, qbo_ids: row.qbo_ids });
            if (data?.success === false) {
              errors.push(data.error || data.message || `Failed to receive ${row.title.toLowerCase()}`);
            } else {
              sent += Number(data?.imported_count || 0) + Number(data?.updated_count || 0);
              (data?.errors || []).forEach((item) => errors.push(item));
            }
          } catch (err) {
            errors.push(`${row.title}: ${integrationError(err)}`);
          }
        }
      }
      const extra = errors.length ? `\n${errors.slice(0, 6).join('\n')}` : '';
      if (!sent && errors.length) {
        setFlash({ type: 'err', text: errors.slice(0, 6).join('\n') });
        return;
      }
      if (errors.length) {
        setFlash({ type: 'err', text: `Synced ${sent} item${sent === 1 ? '' : 's'}.${extra}` });
        return;
      }
      onComplete();
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="pc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="pc-modal" onClick={(event) => event.stopPropagation()}>
        <div className="pc-modal-head">
          <h2>Sync directory</h2>
          <button type="button" className="pc-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="pc-modal-body">
          <p className="pc-muted" style={{ marginTop: 0 }}>
            Choose an integration, then send Claret records or receive records that are not synced.
          </p>
          {integrations.length === 0 ? (
            <p className="pc-flash pc-flash-err">No active integrations found.</p>
          ) : (
            <>
              <div className="pc-sync-options">
                {integrations.map((row) => (
                  <button
                    key={row.vuid}
                    type="button"
                    className={`pc-choice${integrationVuid === row.vuid ? ' is-selected' : ''}`}
                    onClick={() => {
                      setIntegrationVuid(row.vuid);
                      resetChoice();
                    }}
                  >
                    <strong>{row.integration_name}</strong>
                    <span>{integrationLabel(row)}</span>
                  </button>
                ))}
              </div>
              {integration ? (
                <div className="pc-sync-options">
                  <button
                    type="button"
                    className={`pc-choice${direction === 'send' ? ' is-selected' : ''}`}
                    disabled={!sendAllowed}
                    onClick={() => pickDirection('send')}
                  >
                    <strong>Send</strong>
                    <span>
                      {sendAllowed
                        ? `Push unsynced Claret vendors, customers, and employees to ${integrationLabel(integration)}.`
                        : 'Send is only available for the project tool.'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`pc-choice${direction === 'receive' ? ' is-selected' : ''}`}
                    disabled={!receiveAllowed}
                    onClick={() => pickDirection('receive')}
                  >
                    <strong>Receive</strong>
                    <span>
                      {isErpIntegration(integration)
                        ? `Pull unsynced vendors and customers from ${integrationLabel(integration)}.`
                        : `Pull unsynced employees from ${integrationLabel(integration)}.`}
                    </span>
                  </button>
                </div>
              ) : null}
            </>
          )}
          {busy === 'load' ? <div className="pc-empty">Loading unsynced records…</div> : null}
          {direction && busy !== 'load' ? (
            items.length === 0 ? (
              <div className="pc-empty">No unsynced records for this integration.</div>
            ) : (
              <>
                <div className="pc-import-toolbar">
                  <button
                    type="button"
                    className="pc-btn pc-btn-secondary pc-btn-sm"
                    onClick={() => setSelected(items.map((row) => row.id))}
                  >
                    Select all
                  </button>
                  <button type="button" className="pc-btn pc-btn-secondary pc-btn-sm" onClick={() => setSelected([])}>
                    Clear
                  </button>
                  <span className="pc-muted">{selected.length} selected</span>
                </div>
                <div className="pc-import-list">
                  {items.map((row) => (
                    <label key={row.id} className="pc-import-row">
                      <input
                        type="checkbox"
                        checked={selected.includes(row.id)}
                        onChange={() => toggleItem(row.id)}
                      />
                      <span>
                        <strong>{row.title}</strong>
                        <span className="pc-meta">{row.subtitle}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )
          ) : null}
          {flash ? (
            <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`}>{flash.text}</div>
          ) : null}
        </div>
        <div className="pc-modal-actions">
          <button type="button" className="pc-btn pc-btn-secondary" onClick={onClose} disabled={Boolean(busy)}>
            Cancel
          </button>
          <button
            type="button"
            className="pc-btn"
            onClick={runSync}
            disabled={Boolean(busy) || !direction || !selected.length}
          >
            {busy === 'sync' ? 'Syncing…' : direction === 'receive' ? `Receive ${selected.length}` : `Send ${selected.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
